import S, { DataSignal } from 's-js';
import { SynchronousUniqueIndex as uniqueIndex, SynchronousNonuniqueIndex as nonuniqueIndex } from './SynchronousIndex';
import { Monitorable, Monitor, Unmonitor, SynchronousSetMonitor } from './Monitorable';
import { SynchronousSet } from './SynchronousSet';

export abstract class DataCache { }

type timestamp = Date;
type id = string | number;

interface IRelationship<T, Data> {
    ref(item : T) : DataSignal<any>;
    update(item : T, data : Data, timestamp : timestamp) : void;
    attach(item : T) : void;
    detach(item : T) : void;
}

export interface DataCacheCollectionOptions {
    expectEmptyIds? : boolean;
}

export class DataCacheCollection<T, Data> {
    index = uniqueIndex(this.key);
    private _items = Monitorable(SynchronousSet<T>(), [this.index as SynchronousSetMonitor<T>]);
    items: Monitorable<() => Set<T>, SynchronousSetMonitor<T>> = this._items; // public version doesn't allow mutation

    private timestamps : { [id : string] : timestamp | undefined } = {};

    private tempcachedirty = S.data(true);
    private tempcache = S.on<{ [id : string] : T | undefined}>(this.tempcachedirty, () => ({}));

    relationships = [] as IRelationship<T, Data>[];

    private readonly expectEmptyIds : boolean;

    constructor(
        private Model : { new(data: Data) : T },
        private updateItem : (item : T, data : Data) => void,
        public key : (item : T) => id,
        public dataKey : (item : Data) => id,
        options? : DataCacheCollectionOptions
    ) {
        this.expectEmptyIds = !!(options && options.expectEmptyIds);
    }
    
    update1(timestamp: timestamp | null, update: Data, template : T | null = null) : T {
        return this.update(timestamp || new Date(0), [update], [], [template])[0];
    }

    update(timestamp : timestamp, updates : Data[], deletes : id[], templates : (T | null | undefined)[] = []) : T[] {
        return S.freeze(() => {
            var updated : T[] = [],
                tempcache = S.sample(this.tempcache),
                data : Data,
                id : id,
                ts : timestamp | undefined,
                item : T | null,
                template : T | null | undefined,
                i : number, j : number;
            
            for (i = 0; i < updates.length; i++) {
                data = updates[i];
                id   = this.dataKey(data);

                // TH: Providing a way to disable this warning on a collection by collection basis.
                // This makes the warning more meaningful, since otherwise certain types will always trigger it.
                if (!this.expectEmptyIds && (id === 0 || id === '')) console.warn("DataCacheCollection: possible unset id - " + id);

                ts   = this.timestamps[id];
                item = S.sample(this.index(id)) || tempcache[id] || null;
                template = templates[i];

                if (item && template && item !== template) {
                    throw new Error("DataCacheCollection: cannot used supplied template, as entity is already in cache as a different object");
                }

                if (!ts || ts <= timestamp) {
                    this.timestamps[id] = timestamp;

                    if (item) {
                        this.updateItem(item, data);
                    } else {
                        if (template) this.updateItem(item = template, data);
                        else item = new this.Model(data);

                        this._items.add(item);
                        tempcache[id] = item;
                        this.tempcachedirty(true);

                        for (j = 0; j < this.relationships.length; j++) {
                            this.relationships[j].attach(item);
                        }
                    }
                }
                
                if (item) {
                    for (j = 0; j < this.relationships.length; j++) {
                        this.relationships[j].update(item, data, timestamp);
                    }
                    
                    updated.push(item);
                }
            }
            
            for (i = 0; i < deletes.length; i++) {
                id   = deletes[i];
                ts   = this.timestamps[id];
                item = S.sample(this.index(id));
                
                if (item && (!ts || ts <= timestamp)) {
                    this.timestamps[id] = timestamp;
                    for (j = 0; j < this.relationships.length; j++) {
                        this.relationships[j].detach(item);
                    }
                    this._items.delete(item);
                }
            }
            
            return updated;
        });
    }

    attach(item : T, refs? : DataSignal<any>[], autoDetach = true) {
        const relationships = this.refRelationships(item, refs);
        for (let i = 0; i < relationships.length; i++) {
            relationships[i].attach(item);
        }
        if (autoDetach) S.cleanup(() => this.detach(item, refs));
    }

    detach(item : T, refs? : DataSignal<any>[]) {
        const relationships = this.refRelationships(item, refs);
        for (let i = 0; i < relationships.length; i++) {
            relationships[i].detach(item);
        }
    }

    private refRelationships(item : T, refs? : DataSignal<any>[]) {
        if (!refs) return this.relationships;
        const rels = [] as IRelationship<T, Data>[];
        for (let i = 0; i < this.relationships.length; i++) {
            let relationship = this.relationships[i];
            if (refs.indexOf(relationship.ref(item)) !== -1)
                rels.push(relationship);
        }
        return rels;
    }

    timestamp(key : id) {
        return this.timestamps[key];
    }
}

abstract class Relationship<A, AData, B, BData, U> {
    constructor(
        public from : DataCacheCollection<A, AData>,
        public to : DataCacheCollection<B, BData>,
        public ref : (item : A) => DataSignal<U>,
    ) { 
        from.relationships.push(this);
    }

    abstract update(item: A, data : AData, timestamp : timestamp) : void;

    abstract attach(item : A) : void;

    abstract detach(item : A) : void;
}

const DataCacheDisposers = new WeakMap<() => any, () => void>();

abstract class IndexRelationship<A, AData, B, BData, U, K> extends Relationship<A, AData, B, BData, U> {
    constructor(
        from : DataCacheCollection<A, AData>,
        to : DataCacheCollection<B, BData>,
        ref : (item : A) => DataSignal<U>,
        public key : (item : A) => K,
        public index : (key : K) => () => U
    ) { super(from, to, ref); }

    attach(item: A) {
        const
            ref        = this.ref(item),
            index      = this.index,
            key        = this.key;

        S.root(function (dispose) {
            DataCacheDisposers.set(ref, dispose);
            return S(() => {
                const values = index(key(item))();
                return ref(values);
            });
        });
    }

    detach(item: A) { 
        const
            ref = this.ref(item),
            disposer = DataCacheDisposers.get(ref);

        if (disposer) {
            disposer();
            DataCacheDisposers.delete(ref);
        }
    }
}

class SingleRelationship<A, AData, B, BData, K> extends IndexRelationship<A, AData, B, BData, B | null, K> {
    update(item: A, data : AData, timestamp : timestamp) {
        var refData = this.ref(data as any) as any as BData; // NOT typesafe -- applying model ref selector to data to see if we can get ref data

        if (refData === undefined || refData === null) return;
        
        this.to.update1(timestamp, refData);
    }
}

class MultiRelationship<A, AData, B, BData, K> extends IndexRelationship<A, AData, B, BData, B[], K> {
    update(item: A, data : AData, timestamp : timestamp) {
        var updates = this.ref(data as any) as any as BData[]; // NOT typesafe -- applying model ref selector to data to see if we can get ref data

        if (!Array.isArray(updates)) return;

        var updatesIds : { [id : string] : boolean } = {},
            id : id;

        for (var i = 0; i < updates.length; i++) {
            id = this.to.dataKey(updates[i]);
            updatesIds[id] = true;
        }

        var current = S.sample(this.ref(item)),
            deletes : id[] = [];

        for (i = 0; i < current.length; i++) {
            id = this.to.key(current[i]);
            if (!updatesIds[id]) {
                deletes.push(id);
            }
        }

        if (updates.length || deletes.length) this.to.update(timestamp, updates, deletes);
    }
}

export class ForeignKeyRelationship<P, PData, C, CData> {
    parentToChild: MultiRelationship<P, PData, C, CData, id> | null = null;
    childToParent: SingleRelationship<C, CData, P, PData, id | null> | null = null;

    constructor(
        parent : DataCacheCollection<P, PData>,
        children : DataCacheCollection<C, CData>,
        childrenRef: ((item : P) => DataSignal<C[]>) | null,
        parentRef: ((item : C) => DataSignal<P | null>) | null,
        parentId: (item : C) => id | null
    ) { 
        if (childrenRef) {
            let parentChildren = nonuniqueIndex(parentId, children.items);
            Monitor(children.items, parentChildren as SynchronousSetMonitor<C>);
            this.parentToChild = new MultiRelationship(parent, children, childrenRef, parent.key, id => () => Array.from(parentChildren(id)()));
        }
        if (parentRef) this.childToParent = new SingleRelationship(children, parent, parentRef, parentId, onNullKey(parent.index, returnNull));
    }
}

export class OneToOneRelationship<D, DData, M, MData> {
    dependent: SingleRelationship<D, DData, M, MData, id | null> | null = null;
    master: SingleRelationship<M, MData, D, DData, id> | null = null;

    constructor(
        dependent: DataCacheCollection<D, DData>,
        master: DataCacheCollection<M, MData>,
        masterRef: ((item : D) => () => M | null) | null,
        dependentRef: ((item : M) => () => D | null) | null,
        masterId: (item : D) => id | null
    ) { 
        if (masterRef) this.dependent = new SingleRelationship(dependent, master, masterRef, masterId, onNullKey(master.index, returnNull));
        if (dependentRef) {
            let dependentForMaster : uniqueIndex<D>;
            if (masterId === dependent.key) {
                dependentForMaster = dependent.index;
            } else {
                dependentForMaster = uniqueIndex(masterId, dependent.items);
                Monitor(dependent.items, dependentForMaster as SynchronousSetMonitor<D>);
            }
            this.master = new SingleRelationship(master, dependent, dependentRef, master.key, dependentForMaster);
        }
    }
}

export class SharedKeyRelationship<D, DData, M, MData> extends OneToOneRelationship<D, DData, M, MData> {
    constructor(
        dependent: DataCacheCollection<D, DData>,
        master: DataCacheCollection<M, MData>,
        masterRef: ((item : D) => () => M | null) | null,
        dependentRef: ((item : M) => () => D | null) | null
    ) {
        super(dependent, master, masterRef, dependentRef, dependent.key);
    }
}

export class QueryResultsRelationship<Q, QData, R, RData> extends Relationship<Q, QData, R, RData, R[]> {
    constructor(
        query : DataCacheCollection<Q, QData>,
        results : DataCacheCollection<R, RData>,
        resultsRef : (item : Q) => (v? : R[]) => R[]
    ) { super(query, results, resultsRef); }

    attach(item : Q) { }

    detach(item : Q) { }

    update(item: Q, data : QData, timestamp : timestamp) {
        let updateData = this.ref(data as any) as any as RData[]; // NOT typesafe -- applying model ref selector to data to see if we can get ref data

        if (!Array.isArray(updateData)) return;

        let updates = this.to.update(timestamp, updateData, []),
            ref = this.ref(item),
            current = S.sample(ref);
        
        if (!arrayEq(current, updates)) ref(updates);
    }
}

export class QueryResultRelationship<Q, QData, R, RData> extends Relationship<Q, QData, R, RData, R | null> {
    constructor(
        query : DataCacheCollection<Q, QData>,
        result : DataCacheCollection<R, RData>,
        ref : (item : Q) => (v? : R | null) => R | null
    ) { super(query, result, ref); }

    attach(item : Q) { }

    detach(item : Q) { }

    update(item: Q, data : QData, timestamp : timestamp) {
        let updateData = this.ref(data as any) as any as RData; // NOT typesafe -- applying model ref selector to data to see if we can get ref data

        if (updateData === undefined || updateData === null) return;

        let update = this.to.update1(timestamp, updateData),
            ref = this.ref(item),
            current = S.sample(ref);
        
        if (current !== update) ref(update);
    }
}

function arrayEq<T>(a : T[], b : T[]) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) return false;
    }
    return true;
}

// Add handling for null keys to a unique index that expects keys to be non-null.
function onNullKey<U>(index : (key : id) => () => U, whenNull : () => U) {
    return (key : id | null) => key === null ? whenNull : index(key);
}

function returnNull() { return null; }



// WEBPACK FOOTER //
// ./lib/DataCache.ts